import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Wrench } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const AdjustmentsPage = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [returns, setReturns] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    pharmacy_id: '',
    product_id: '',
    batch_id: '',
    quantity_delta: 0,
    _absQty: '',
    _direction: 'add',
    reason: '',
    movement_type: 'manual_adjustment',
    notes: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [adjRes, prodRes, pharmRes, batchRes, retRes, salesRes] = await Promise.all([
        api.get('/adjustments'),
        api.get('/products'),
        api.get('/pharmacies'),
        api.get('/batches'),
        api.get('/returns'),
        api.get('/sales'),
      ]);
      setAdjustments(adjRes.data);
      setProducts(prodRes.data);
      setPharmacies(pharmRes.data);
      setBatches(batchRes.data);
      setReturns(retRes.data);
      setSales(salesRes.data);
    } catch (error) { console.error(error); }
  };

  const handleSubmit = async () => {
    if (!form.product_id || !form.batch_id || !form.reason.trim()) {
      toast.error('Product, batch, and reason are required');
      return;
    }
    const absQty = parseInt(form._absQty);
    if (isNaN(absQty) || absQty <= 0) {
      toast.error('Quantity must be a positive number');
      return;
    }
    const qty = form._direction === 'remove' ? -absQty : absQty;
    if (!window.confirm(`Confirm ${form._direction === 'remove' ? 'REMOVING' : 'ADDING'} ${absQty} unit(s)?`)) return;
    try {
      await api.post('/adjustments', {
        pharmacy_id: form.pharmacy_id || null,
        product_id: form.product_id,
        batch_id: form.batch_id,
        quantity_delta: qty,
        reason: form.reason,
        movement_type: form.movement_type,
        notes: form.notes,
      });
      toast.success('Adjustment recorded');
      setShowModal(false);
      setForm({ pharmacy_id: '', product_id: '', batch_id: '', quantity_delta: 0, _absQty: '', _direction: 'add', reason: '', movement_type: 'manual_adjustment', notes: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const productBatches = batches.filter(b => b.product_id === form.product_id);
  const getProduct = (id) => products.find(p => p.id === id);
  const getPharmacy = (id) => pharmacies.find(p => p.id === id);
  const getBatch = (id) => batches.find(b => b.id === id);

  const badgeColor = (type) => {
    if (type === 'sample') return 'bg-purple-50 text-purple-700 border-purple-200';
    if (type === 'recall') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Stock Adjustments</h1>
        <Button data-testid="new-adjustment-btn" onClick={() => setShowModal(true)} style={{ backgroundColor: '#1E3A8A' }}>
          <Plus size={16} className="mr-2" /> New Adjustment
        </Button>
      </div>

      <Tabs defaultValue="adjustments">
        <TabsList>
          <TabsTrigger value="adjustments" data-testid="tab-adjustments">Stock Adjustments</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns">Customer Returns</TabsTrigger>
        </TabsList>

        <TabsContent value="adjustments">
          <Card className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-3 font-semibold">Date</th>
                  <th className="pb-3 font-semibold">Type</th>
                  <th className="pb-3 font-semibold">Location</th>
                  <th className="pb-3 font-semibold">Product</th>
                  <th className="pb-3 font-semibold">Batch</th>
                  <th className="pb-3 font-semibold text-right">Before</th>
                  <th className="pb-3 font-semibold text-right">Delta</th>
                  <th className="pb-3 font-semibold text-right">After</th>
                  <th className="pb-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => {
                  const prod = getProduct(a.product_id);
                  const batch = getBatch(a.batch_id);
                  return (
                    <tr key={a.id} data-testid={`adj-${a.id}`} className="border-b border-slate-100">
                      <td className="py-3 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="py-3"><Badge className={badgeColor(a.movement_type)}>{a.movement_type.replace('_', ' ')}</Badge></td>
                      <td className="py-3">{a.pharmacy_id ? getPharmacy(a.pharmacy_id)?.name : 'Warehouse'}</td>
                      <td className="py-3">{prod?.name || '-'}</td>
                      <td className="py-3 font-mono text-xs">{batch?.batch_number || '-'}</td>
                      <td className="py-3 text-right">{a.before_qty}</td>
                      <td className={`py-3 text-right font-bold ${a.quantity_delta > 0 ? 'text-green-600' : 'text-red-600'}`}>{a.quantity_delta > 0 ? '+' : ''}{a.quantity_delta}</td>
                      <td className="py-3 text-right">{a.after_qty}</td>
                      <td className="py-3 text-xs text-slate-700 max-w-xs">{a.reason}</td>
                    </tr>
                  );
                })}
                {adjustments.length === 0 && (
                  <tr><td colSpan={9} className="py-8 text-center text-slate-500">No adjustments yet</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="returns">
          <Card className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-3 font-semibold">Date</th>
                  <th className="pb-3 font-semibold">Pharmacy</th>
                  <th className="pb-3 font-semibold">Product / Batch</th>
                  <th className="pb-3 font-semibold text-right">Qty Returned</th>
                  <th className="pb-3 font-semibold text-right">Amount (RM)</th>
                  <th className="pb-3 font-semibold">Original Sale</th>
                  <th className="pb-3 font-semibold">Effective Period</th>
                  <th className="pb-3 font-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {returns.map((r) => {
                  const origSale = sales.find(s => s.id === r.sale_id);
                  // Build per-line deduction summary for display
                  const deds = origSale?.deductions || [];
                  const ratio = origSale ? (r.quantity / (origSale.quantity || 1)) : 0;
                  return (
                    <tr key={r.id} data-testid={`ret-${r.id}`} className="border-b border-slate-100">
                      <td className="py-3 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-3">{getPharmacy(r.pharmacy_id)?.name || '-'}</td>
                      <td className="py-3 text-xs">
                        {deds.length ? deds.map((d, i) => {
                          const p = getProduct(d.product_id);
                          const unit_qty = Math.round((d.quantity || 0) * ratio);
                          return <div key={i}>{p?.name || d.product_id} × {unit_qty} {d.is_foc && <span className="text-orange-600">(FOC)</span>}</div>;
                        }) : <span className="text-slate-400">-</span>}
                      </td>
                      <td className="py-3 text-right font-semibold">{r.quantity}</td>
                      <td className="py-3 text-right font-bold text-red-600">-{Number(r.total_reversed || 0).toFixed(2)}</td>
                      <td className="py-3 text-xs font-mono text-slate-500">{r.sale_id?.slice(0, 8)}…</td>
                      <td className="py-3 text-xs">
                        <Badge className={r.handling === 'next_month_adjustment' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}>
                          {String(r.effective_period_month || '?').padStart(2, '0')}/{r.effective_period_year || '?'}
                          {r.handling === 'next_month_adjustment' && ' (carry)'}
                        </Badge>
                      </td>
                      <td className="py-3 text-xs text-slate-700 max-w-xs">{r.reason}</td>
                    </tr>
                  );
                })}
                {returns.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-slate-500">No returns yet</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Stock Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Movement Type</Label>
              <Select value={form.movement_type} onValueChange={(v) => setForm({ ...form, movement_type: v })}>
                <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_adjustment">Manual Adjustment</SelectItem>
                  <SelectItem value="sample">Sample</SelectItem>
                  <SelectItem value="recall">Recall</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location (leave empty for Warehouse)</Label>
              <Select value={form.pharmacy_id || 'warehouse'} onValueChange={(v) => setForm({ ...form, pharmacy_id: v === 'warehouse' ? '' : v })}>
                <SelectTrigger data-testid="adj-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                  {pharmacies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Product</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, batch_id: '' })}>
                <SelectTrigger data-testid="adj-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Batch</Label>
              <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                <SelectTrigger data-testid="adj-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  {productBatches.map(b => <SelectItem key={b.id} value={b.id}>{b.batch_number} — Exp {String(b.expiry_month).padStart(2,'0')}/{b.expiry_year}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Direction</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  data-testid="adj-direction-add"
                  onClick={() => setForm({ ...form, _direction: 'add' })}
                  variant={form._direction === 'remove' ? 'outline' : 'default'}
                  style={form._direction !== 'remove' ? { backgroundColor: '#059669' } : {}}
                  className="flex-1"
                >+ Add stock</Button>
                <Button
                  type="button"
                  data-testid="adj-direction-remove"
                  onClick={() => setForm({ ...form, _direction: 'remove' })}
                  variant={form._direction === 'remove' ? 'default' : 'outline'}
                  style={form._direction === 'remove' ? { backgroundColor: '#DC2626' } : {}}
                  className="flex-1"
                >− Remove stock</Button>
              </div>
            </div>
            <div>
              <Label>Quantity ({form._direction === 'remove' ? 'will be subtracted' : 'will be added'})</Label>
              <Input data-testid="adj-qty" type="number" min="1" value={form._absQty ?? ''} onChange={(e) => setForm({ ...form, _absQty: e.target.value })} placeholder="e.g. 5" />
              {form.product_id && form.batch_id && form._absQty && (() => {
                const b = batches.find(x => x.id === form.batch_id);
                const beforeQty = b?.remaining ?? 0;
                const delta = (parseInt(form._absQty) || 0) * (form._direction === 'remove' ? -1 : 1);
                const afterQty = beforeQty + delta;
                return (
                  <p className={`text-xs mt-1 ${afterQty < 0 ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                    Before {beforeQty} → After {afterQty} {afterQty < 0 && '(will be rejected — stock cannot go negative)'}
                  </p>
                );
              })()}
            </div>
            <div>
              <Label>Reason (required)</Label>
              <Textarea data-testid="adj-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Explain why this adjustment is being made..." rows={3} />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input data-testid="adj-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button data-testid="submit-adjustment-btn" onClick={handleSubmit} style={{ backgroundColor: '#1E3A8A' }}>Record Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
